/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Mock Axios Clients
 * axios 인터페이스를 모방하여 실제 API 호출 없이 목업 데이터를 반환합니다.
 */
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import type { CreateDeploymentRequest, DeploymentsByRunIdsRequest, UpdateDeploymentRequest } from '@/shared/api/deploy';

import { MockRouter } from './mockRouter';
import { getAssetDetailMockData, getTableColumnsMockData } from './stores/assetDetailMockStore';
import {
  createConnectorMockData,
  deleteConnectorsMockData,
  getConnectorMockData,
  getConnectorsMockData,
  getConnectorTypeSchemaMockData,
  getConnectorTypesMockData,
  updateConnectorMockData,
  updateConnectorsStatusMockData,
} from './stores/connectorMockStore';
import { getDagDataSourcesMockData } from './stores/datasourceMockStore';
import {
  createDeploymentMock,
  getDeploymentsByRunIdsMock,
  getDeploymentStatusMock,
  updateDeploymentMock,
} from './stores/deploymentMockStore';
import {
  getDomainDetailByFqn,
  getDomainHierarchyMockData,
  getDomainUsersMockData,
  getSubDomainsByParentFqn,
  getTeamOwnersMockData,
  getUserOwnersMockData,
} from './stores/domainMockStore';
import {
  cancelRunExecutionMock,
  compareRunsMockData,
  createExperimentMock,
  createProjectMock,
  createRunMock,
  executeRunMock,
  getExperimentAssetDetail,
  getExperimentsMockData,
  getProjectMockData,
  getProjectsExperimentsMockData,
  getRunDetailMock,
  getRunExecutionMock,
  getRunLatestExecutionMock,
  getRunMetricsMock,
  getRunsMockData,
  loadCustomRuns,
  searchIntegrationMockData,
  startRunExecutionMock,
  updateExperimentMock,
  updateRunNameMock,
} from './stores/experimentMockStore';
import {
  getDirectChildrenTerms,
  getGlossaryCategoriesMockData,
  getGlossaryCategoryByFqn,
  getGlossaryTermByFqn,
  getGlossaryTermsMockData,
} from './stores/glossaryMockStore';
import { getChartMockData, getDataAssetsTrendMockData } from './stores/homeStatsMockStore';
import { getAssetLineageMockData, getPlatformLineageMockData } from './stores/lineageMockStore';
import { getPrometheusInstantMockData, getPrometheusRangeMockData } from './stores/monitoringMockStore';
import {
  getColumnQualityMockData,
  getDataZoneRowCountMockData,
  getQualityCategoriesMockData,
  getQualityTrendMockData,
  getStatsTablesMockData,
  getTableQualityMockData,
} from './stores/qualityDashboardMockStore';
import {
  getDomainAggregationsMockData,
  getInitialAggregationsMockData,
  searchMockData,
} from './stores/searchMockStore';
import {
  getTagCategoriesMockData,
  getTagCategoryByName,
  getTagDetailByFqn,
  getTagsMockData,
} from './stores/tagMockStore';
import {
  getDatabasesMockData,
  getInitialTreeMockData,
  getSchemasMockData,
  getServiceTypeChildrenMockData,
  getTablesMockData,
} from './stores/treeMockStore';
import {
  createUserManagementRoleMockData,
  createUserManagementUserMockData,
  deleteUserManagementUserMockData,
  getUserManagementRolesMockData,
  getUserManagementUsersMockData,
  updateUserManagementUserRolesMockData,
} from './stores/userManagementMockStore';

const createMockResponse = <T>(data: T, status: number = 200): AxiosResponse<T> => ({
  data,
  status,
  statusText: 'OK',
  headers: {
    'content-type': 'application/json',
  } as any,
  config: {} as any,
});

const delay = (ms: number = 100) => new Promise((resolve) => setTimeout(resolve, ms));
const CONNECTOR_MUTATION_DELAY_MS = 2000;

const parseMockRequestData = <T extends Record<string, any>>(data: unknown): T => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as T;
    } catch {
      return {} as T;
    }
  }

  return (data ?? {}) as T;
};

const parseQueryParams = (params: any): Record<string, any> => {
  if (!params) return {};

  const parsed: Record<string, any> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (key === 'query_filter' && typeof value === 'string') {
      try {
        parsed.query_filter = JSON.parse(value);
      } catch {
        parsed.query_filter = value;
      }
    } else if (key === 'q' && typeof value === 'string') {
      parsed.q = value;
    } else {
      parsed[key] = value;
    }
  });

  return parsed;
};

const extractFiltersFromQueryFilter = (queryFilter: any): any => {
  const filters: any = {
    dataSource: [],
    assetType: [],
    domain: [],
    owner: [],
    tier: [],
    tags: [],
    certification: [],
    quickFilter: {},
  };

  if (!queryFilter?.query?.bool) return filters;

  const mustClauses = queryFilter.query.bool.must || [];

  mustClauses.forEach((clause: any) => {
    if (clause.bool?.should) {
      const shouldClauses = clause.bool.should;

      shouldClauses.forEach((shouldClause: any) => {
        if (shouldClause.term) {
          const [field, value] = Object.entries(shouldClause.term)[0] as [string, string];

          if (field === 'entityType' || field === 'entityType.keyword') {
            filters.assetType.push(value);
          } else if (field === 'domains.name.keyword') {
            filters.domain.push(value);
          } else if (field === 'domains.fullyQualifiedName') {
            filters.domain.push(value);
          } else if (field === 'owners.displayName.keyword') {
            filters.owner.push(value);
          } else if (field === 'tier.tagFQN') {
            filters.tier.push(value);
          } else if (field === 'tags.tagFQN') {
            filters.tags.push(value);
          } else if (field === 'certification.tagLabel.tagFQN') {
            filters.certification.push(value);
          } else if (field === 'serviceType' || field === 'serviceType.keyword') {
            filters.dataSource.push(value);
          } else if (field === 'service.displayName.keyword') {
            filters.dataSource.push(value);
          }
        } else if (shouldClause.prefix) {
          const [field, value] = Object.entries(shouldClause.prefix)[0] as [string, string];
          if (field === 'domains.fullyQualifiedName') {
            filters.domain.push(value);
          }
        }
      });
    } else if (clause.term) {
      const [field, value] = Object.entries(clause.term)[0] as [string, string];

      if (field === 'tags.tagFQN') {
        filters.tags.push(value);
      } else if (field === 'certification.tagLabel.tagFQN') {
        filters.certification.push(value);
      } else if (field === 'serviceType' || field === 'serviceType.keyword') {
        filters.quickFilter.serviceType = value;
      } else if (field === 'service.displayName.keyword' || field === 'service.name.keyword') {
        filters.quickFilter.serviceName = value;
      } else if (field === 'database.displayName' || field === 'database.name.keyword') {
        filters.quickFilter.databaseName = value;
      } else if (field === 'databaseSchema.displayName' || field === 'databaseSchema.name.keyword') {
        filters.quickFilter.schemaName = value;
      } else if (field === 'entityType' || field === 'entityType.keyword') {
        filters.quickFilter.entityType = value;
      }
    }
  });

  // quickFilter가 비어있으면 제거
  if (Object.keys(filters.quickFilter).length === 0) {
    delete filters.quickFilter;
  }

  return filters;
};

export const createMockOpenMetadataClient = (): Partial<AxiosInstance> => {
  const router = new MockRouter();

  router.register('GET', '/search/query', async (_url, config) => {
    await delay(50);

    const params = parseQueryParams(config?.params);
    const query = params.q === '*' ? '' : params.q || '';
    const from = params.from !== undefined ? Number(params.from) : 0;
    const size = params.size !== undefined ? Number(params.size) : 15;
    const sortField = params.sort_field;
    const sortBy = sortField === 'name.keyword' ? 'name' : sortField === 'updatedAt' ? 'lastUpdated' : 'relevance';

    const filters = extractFiltersFromQueryFilter(params.query_filter);

    const isAggregationOnly = from === 0 && size === 0;

    if (isAggregationOnly) {
      // aggregation만 요청하는 경우
      // tags 필터가 있으면 필터링된 결과의 총 개수 반환 (용어/태그 자산 카운트용)
      if (filters.tags && filters.tags.length > 0) {
        const response = searchMockData(query, filters, from, size, sortBy);
        return createMockResponse(response);
      }
      // 필터가 없으면 초기 aggregations 반환
      const response = getInitialAggregationsMockData();
      // console.log('🔍 Aggregations response:', {
      //   params: { query, index, deleted: params.deleted },
      //   hasTagsAgg: !!response.aggregations?.['sterms#tags.tagFQN'],
      //   tagsCount: response.aggregations?.['sterms#tags.tagFQN']?.buckets?.length,
      //   allAggKeys: Object.keys(response.aggregations || {}),
      // });
      return createMockResponse(response);
    }

    const response = searchMockData(query, filters, from, size, sortBy);
    return createMockResponse(response);
  });

  router.register('GET', '/search/aggregate', async (_url, _config) => {
    await delay(30);
    const response = getDomainAggregationsMockData();
    return createMockResponse(response);
  });

  router.register('GET', '/classifications', async (_url, config) => {
    await delay(30);
    const limit = Number(config?.params?.limit || 1000);
    const response = getTagCategoriesMockData(limit);
    return createMockResponse(response);
  });

  router.register('GET', '/classifications/name/:name', async (url, _config) => {
    await delay(30);
    const urlPath = url.split('/classifications/name/')[1];
    const categoryName = decodeURIComponent(urlPath.split('?')[0]);
    const category = getTagCategoryByName(categoryName);

    if (!category) {
      return createMockResponse({ message: 'Classification not found' }, 404);
    }

    return createMockResponse(category);
  });

  router.register('GET', '/tags', async (_url, config) => {
    await delay(30);
    const parent = config?.params?.parent;
    const limit = Number(config?.params?.limit || 1000);
    const response = getTagsMockData(parent, limit);
    // console.log('Tags API response:', { parent, limit, count: response.data.length, sample: response.data[0] });
    return createMockResponse(response);
  });

  router.register('GET', '/tags/name/:fqn', async (url, _config) => {
    await delay(30);
    const urlPath = url.split('/tags/name/')[1];
    const fqn = decodeURIComponent(urlPath.split('?')[0]);
    const tag = getTagDetailByFqn(fqn);

    if (!tag) {
      return createMockResponse({ message: 'Tag not found' }, 404);
    }

    return createMockResponse(tag);
  });

  router.register('GET', '/glossaries', async (_url, config) => {
    await delay(30);
    const limit = Number(config?.params?.limit || 1000);
    const response = getGlossaryCategoriesMockData(limit);
    return createMockResponse(response);
  });

  router.register('GET', '/glossaryTerms', async (_url, config) => {
    await delay(30);
    const glossary = config?.params?.glossary;
    const directChildrenOf = config?.params?.directChildrenOf;
    const limit = Number(config?.params?.limit || 1000);

    // directChildrenOf 파라미터가 있으면 직계 자식만 반환
    if (directChildrenOf) {
      const response = getDirectChildrenTerms(directChildrenOf, limit);
      return createMockResponse(response);
    }

    // glossary 파라미터가 있으면 해당 용어집의 모든 용어 반환
    const response = getGlossaryTermsMockData(glossary, limit);
    return createMockResponse(response);
  });

  router.register('GET', '/glossaryTerms/name/:fqn', async (url, _config) => {
    await delay(50);
    const urlPath = url.split('/glossaryTerms/name/')[1];
    const fqn = decodeURIComponent(urlPath.split('?')[0]);
    const term = getGlossaryTermByFqn(fqn);
    if (!term) {
      return createMockResponse({ message: 'Glossary term not found' }, 404);
    }
    return createMockResponse(term);
  });

  router.register('GET', '/glossaries/name/:fqn', async (url, _config) => {
    await delay(50);
    const urlPath = url.split('/glossaries/name/')[1];
    const fqn = decodeURIComponent(urlPath.split('?')[0]);
    const category = getGlossaryCategoryByFqn(fqn);
    if (!category) {
      return createMockResponse({ message: 'Glossary category not found' }, 404);
    }
    return createMockResponse(category);
  });

  router.register('GET', '/domains/hierarchy', async (_url, _config) => {
    await delay(30);
    const response = getDomainHierarchyMockData();
    return createMockResponse(response);
  });

  router.register('GET', '/domains', async (_url, _config) => {
    await delay(30);
    const response = getDomainHierarchyMockData();
    return createMockResponse(response);
  });

  router.register('GET', '/domains/name/:fqn', async (url, _config) => {
    await delay(50);
    const urlPath = url.split('/domains/name/')[1];
    const fqn = decodeURIComponent(urlPath.split('?')[0]);
    const domainDetail = getDomainDetailByFqn(fqn);

    if (!domainDetail) {
      return createMockResponse({ message: 'Domain not found' }, 404);
    }

    return createMockResponse(domainDetail);
  });

  router.register('GET', '/users', async (_url, config) => {
    await delay(30);
    const limit = Number(config?.params?.limit || 25);
    const response = getDomainUsersMockData(limit);
    return createMockResponse(response);
  });

  router.register('GET', '/tables/name/:fqn/columns', async (url, _config) => {
    await delay(60);
    const urlPath = url.split('/tables/name/')[1];
    const fqn = decodeURIComponent(urlPath.replace('/columns', '').split('?')[0]);
    const columnsData = getTableColumnsMockData(fqn);
    return createMockResponse(columnsData);
  });

  router.register('GET', '/tables/name/:fqn', async (url, _config) => {
    await delay(80);
    const urlPath = url.split('/tables/name/')[1];
    const fqn = decodeURIComponent(urlPath.split('?')[0]);
    const detail = getAssetDetailMockData(fqn, 'table');

    if (!detail) {
      return createMockResponse({ message: 'Table not found' }, 404);
    }

    return createMockResponse(detail);
  });

  router.register('GET', '/databases/name/:fqn', async (url, _config) => {
    await delay(60);
    const fqn = decodeURIComponent(url.split('/databases/name/')[1].split('?')[0]);
    const detail = getAssetDetailMockData(fqn, 'database');

    if (!detail) {
      return createMockResponse({ message: 'Database not found' }, 404);
    }

    return createMockResponse(detail);
  });

  router.register('GET', '/databaseSchemas/name/:fqn', async (url, _config) => {
    await delay(60);
    const fqn = decodeURIComponent(url.split('/databaseSchemas/name/')[1].split('?')[0]);
    const detail = getAssetDetailMockData(fqn, 'databaseSchema');

    if (!detail) {
      return createMockResponse({ message: 'Schema not found' }, 404);
    }

    return createMockResponse(detail);
  });

  router.register('GET', '/containers/name/:fqn', async (url, _config) => {
    await delay(60);
    const fqn = decodeURIComponent(url.split('/containers/name/')[1].split('?')[0]);
    const detail = getAssetDetailMockData(fqn, 'container');

    if (!detail) {
      return createMockResponse({ message: 'Container not found' }, 404);
    }

    return createMockResponse(detail);
  });

  router.register('GET', '/topics/name/:fqn', async (url, _config) => {
    await delay(60);
    const fqn = decodeURIComponent(url.split('/topics/name/')[1].split('?')[0]);
    const detail = getAssetDetailMockData(fqn, 'topic');

    if (!detail) {
      return createMockResponse({ message: 'Topic not found' }, 404);
    }

    return createMockResponse(detail);
  });

  router.register('GET', '/lineage/getPlatformLineage', async (_url, config) => {
    await delay(80);
    const params = config?.params || {};
    const view = params.view || 'service';

    const response = getPlatformLineageMockData(view);
    return createMockResponse(response);
  });

  router.register('GET', '/analytics/dataInsights/system/charts/name/:chartName/data', async (url, config) => {
    await delay(50);
    const urlPath = url.split('/charts/name/')[1];
    const chartName = urlPath.split('/')[0];
    const params = config?.params || {};
    const startDate = Number(params.start || 0);
    const endDate = Number(params.end || Date.now());

    const response = getChartMockData(chartName, startDate, endDate);
    return createMockResponse(response);
  });

  router.register('GET', '/lineage/getLineage', async (_url, config) => {
    await delay(60);
    const params = config?.params || {};
    const fqn = params.fqn;

    if (!fqn) {
      return createMockResponse({ message: 'fqn is required' }, 400);
    }

    const lineageData = getAssetLineageMockData(fqn);
    return createMockResponse(lineageData);
  });

  return {
    get<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      const params = parseQueryParams(config?.params);

      if (url === '/search/query') {
        const index = params.index || 'dataAsset';
        const from = params.from !== undefined ? Number(params.from) : 0;
        const size = params.size !== undefined ? Number(params.size) : 15;
        const isAggregationOnly = from === 0 && size === 0;
        const isTreeInitialAggregationRequest =
          isAggregationOnly &&
          !params.query_filter &&
          (params.track_total_hits !== undefined || params.fetch_source !== undefined);

        // Team search index 처리
        if (index === 'team_search_index') {
          const response = getTeamOwnersMockData();
          return Promise.resolve(createMockResponse(response) as any);
        }

        // User search index 처리
        if (index === 'user_search_index') {
          const response = getUserOwnersMockData();
          return Promise.resolve(createMockResponse(response) as any);
        }

        // Domain search index 특수 처리 (하위 도메인 검색)
        if (index === 'domain_search_index') {
          const query = params.q || '';
          const parentFqnMatch = query.match(/parent\.fullyQualifiedName:"([^"]+)"/);
          if (parentFqnMatch) {
            const parentFqn = parentFqnMatch[1];
            const response = getSubDomainsByParentFqn(parentFqn);
            return Promise.resolve(createMockResponse(response) as any);
          }
        }

        // Tree 전용 특수 처리 (aggregation only + query_filter가 없음)
        if (index === 'dataAsset' && isTreeInitialAggregationRequest) {
          const response = getInitialTreeMockData();
          return Promise.resolve(createMockResponse(response) as any);
        }

        // Tree 노드 확장을 위한 특수 처리 (aggregation only + 특정 조건)
        if (params.query_filter && from === 0 && size === 0) {
          const queryFilter = params.query_filter;
          const mustClauses = queryFilter?.query?.bool?.must || [];

          const serviceTypeClause = mustClauses.find(
            (c: any) =>
              c.term?.['serviceType.keyword'] ||
              c.term?.['serviceType'] ||
              c.bool?.should?.[0]?.term?.['serviceType.keyword'] ||
              c.bool?.should?.[0]?.term?.['serviceType']
          );
          const serviceNameClause = mustClauses.find(
            (c: any) => c.term?.['service.name.keyword'] || c.bool?.should?.[0]?.term?.['service.name.keyword']
          );
          const databaseNameClause = mustClauses.find(
            (c: any) => c.term?.['database.name.keyword'] || c.bool?.should?.[0]?.term?.['database.name.keyword']
          );
          const schemaNameClause = mustClauses.find(
            (c: any) =>
              c.term?.['databaseSchema.name.keyword'] || c.bool?.should?.[0]?.term?.['databaseSchema.name.keyword']
          );

          if (serviceTypeClause && !serviceNameClause && (index === 'dataAsset' || index === 'database_search_index')) {
            let serviceType =
              serviceTypeClause.term?.['serviceType.keyword'] || serviceTypeClause.term?.['serviceType'];

            // entityType -> serviceType 매핑
            if (serviceType === 'database') {
              serviceType = 'databaseService';
            }

            // iceberg serviceType은 그대로 전달
            const response = getServiceTypeChildrenMockData(serviceType);
            return Promise.resolve(createMockResponse(response) as any);
          }

          if (index === 'database' && serviceNameClause) {
            const serviceName = serviceNameClause.term?.['service.name.keyword'];
            const response = getDatabasesMockData(serviceName);
            return Promise.resolve(createMockResponse(response) as any);
          }

          if (index === 'databaseSchema' && databaseNameClause) {
            const serviceName = serviceNameClause.term?.['service.name.keyword'];
            const databaseName = databaseNameClause.term?.['database.name.keyword'];
            const response = getSchemasMockData(serviceName, databaseName);
            return Promise.resolve(createMockResponse(response) as any);
          }

          if (index === 'table' && schemaNameClause) {
            const serviceName = serviceNameClause.term?.['service.name.keyword'];
            const databaseName = databaseNameClause.term?.['database.name.keyword'];
            const schemaName = schemaNameClause.term?.['databaseSchema.name.keyword'];
            const response = getTablesMockData(serviceName, databaseName, schemaName);
            return Promise.resolve(createMockResponse(response) as any);
          }
        }
      }

      return router.match('GET', url, { params: config?.params } as any) as any;
    },
    post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('POST', url, { data, ...config } as any) as any);
    },
    patch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('PATCH', url, { data, ...config } as any) as any);
    },
    put<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('PUT', url, { data, ...config } as any) as any);
    },
    delete<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('DELETE', url, config as any) as any);
    },
  };
};

export const createMockConnectorClient = (): Partial<AxiosInstance> => {
  const router = new MockRouter();

  router.register('GET', '/connector-types', async () => {
    await delay(50);
    return createMockResponse(getConnectorTypesMockData());
  });

  router.register('GET', '/connector-types/:type/schema', async (url) => {
    await delay(50);
    const type = decodeURIComponent(url.split('/connector-types/')[1].split('/schema')[0]);
    return createMockResponse(getConnectorTypeSchemaMockData(type));
  });

  router.register('GET', '/connectors', async (_url, config) => {
    await delay(80);
    const params = config?.params || {};
    const response = getConnectorsMockData({
      ...params,
      page: params.page !== undefined ? Number(params.page) : undefined,
      pageSize: params.pageSize !== undefined ? Number(params.pageSize) : undefined,
      isActive: typeof params.isActive === 'string' ? params.isActive === 'true' : params.isActive,
      status: params.status ? (Array.isArray(params.status) ? params.status : [params.status]) : undefined,
    });
    return createMockResponse(response);
  });

  router.register('GET', '/connectors/:id', async (url) => {
    await delay(50);
    const id = decodeURIComponent(url.split('/connectors/')[1].split('?')[0]);
    const connector = getConnectorMockData(id);

    if (!connector) {
      return createMockResponse({ message: 'Connector not found' }, 404);
    }

    return createMockResponse(connector);
  });

  router.register('POST', '/connectors', async (_url, config) => {
    await delay(CONNECTOR_MUTATION_DELAY_MS);
    const request = parseMockRequestData(config?.data);

    if (!request.name || !request.type || !request.dataInfo) {
      return createMockResponse({ message: 'Connector name, type, and dataInfo are required' }, 400);
    }

    return createMockResponse(createConnectorMockData(request as any), 201);
  });

  router.register('PATCH', '/connectors/:id/config', async (url, config) => {
    await delay(CONNECTOR_MUTATION_DELAY_MS);
    const id = decodeURIComponent(url.split('/connectors/')[1].split('/config')[0]);
    const request = parseMockRequestData(config?.data);
    const updatedConnector = updateConnectorMockData(id, request as any);

    if (!updatedConnector) {
      return createMockResponse({ message: 'Connector not found' }, 404);
    }

    return createMockResponse(updatedConnector);
  });

  router.register('PATCH', '/connectors/status', async (_url, config) => {
    await delay(CONNECTOR_MUTATION_DELAY_MS);
    const request = parseMockRequestData(config?.data);
    updateConnectorsStatusMockData(request as any);
    return createMockResponse({ message: 'Connector statuses updated' });
  });

  router.register('DELETE', '/connectors', async (_url, config) => {
    await delay(CONNECTOR_MUTATION_DELAY_MS);
    const request = parseMockRequestData(config?.data);
    deleteConnectorsMockData(request as any);
    return createMockResponse({ message: 'Connectors deleted' });
  });

  return {
    get<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('GET', url, config as any) as any;
    },
    post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('POST', url, { data, ...config } as any) as any;
    },
    patch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('PATCH', url, { data, ...config } as any) as any;
    },
    put<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('PUT', url, { data, ...config } as any) as any);
    },
    delete<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('DELETE', url, config as any) as any;
    },
  };
};

export const createMockCycloneClient = (): Partial<AxiosInstance> => {
  const router = new MockRouter();

  router.register('GET', '/users', async (_url, config) => {
    await delay(30);
    return createMockResponse(getUserManagementUsersMockData(config?.params || {}));
  });

  router.register('POST', '/users', async (_url, config) => {
    const request = parseMockRequestData(config?.data);
    return createMockResponse(createUserManagementUserMockData(request as any), 201);
  });

  router.register('GET', '/users/roles', async () => {
    await delay(30);
    return createMockResponse(getUserManagementRolesMockData());
  });

  router.register('POST', '/users/roles', async (_url, config) => {
    const request = parseMockRequestData(config?.data);
    return createMockResponse(createUserManagementRoleMockData(request as any), 201);
  });

  router.register('PATCH', '/users/:id/roles', async (url, config) => {
    const userId = Number(url.match(/^\/users\/(\d+)\/roles$/)?.[1]);
    const request = parseMockRequestData(config?.data);
    return createMockResponse(updateUserManagementUserRolesMockData(userId, request as any));
  });

  router.register('DELETE', '/users/:id', async (url) => {
    await delay(100);
    const userId = Number(url.match(/^\/users\/(\d+)$/)?.[1]);
    return createMockResponse(deleteUserManagementUserMockData(userId));
  });

  router.register('GET', '/datasources', async (_url, config) => {
    await delay(50);
    const response = getDagDataSourcesMockData({ maturity: config?.params?.maturity });
    return createMockResponse(response);
  });

  router.register('GET', '/dashboard/entity-counts', async (_url, config) => {
    await delay(50);
    const params = config?.params || {};
    const start = Number(params.start || Date.now() - 7 * 24 * 60 * 60 * 1000);
    const end = Number(params.end || Date.now());
    const period = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    return createMockResponse(getDataAssetsTrendMockData(period));
  });

  router.register('GET', '/projects/tree', async (_url, config) => {
    await delay(100);
    const params = config?.params || {};
    const response = getProjectsExperimentsMockData(params.query);
    return createMockResponse(response);
  });

  router.register('POST', '/projects', async (_url, config) => {
    await delay(150);

    // config.data가 이미 객체인 경우 그대로 사용, 문자열인 경우 파싱
    let data: any;
    if (typeof config?.data === 'string') {
      data = JSON.parse(config.data);
    } else {
      data = config?.data || {};
    }

    const { name, description } = data;

    if (!name) {
      return createMockResponse({ message: 'Project name is required' }, 400);
    }

    const newProject = createProjectMock(name, description);

    return createMockResponse({
      id: newProject.id,
      name: newProject.name,
      description: newProject.description,
    });
  });

  router.register('GET', '/projects/:id', async (url, _config) => {
    await delay(60);
    const projectId = Number(url.split('/projects/')[1].split('?')[0]);
    const response = getProjectMockData(projectId);
    if (!response) {
      return createMockResponse({ message: 'Project not found' }, 404);
    }
    return createMockResponse(response);
  });

  router.register('GET', '/experiments', async (_url, config) => {
    await delay(80);
    const params = config?.params || {};
    const response = getExperimentsMockData({
      projectId: params.projectId ? Number(params.projectId) : undefined,
      query: params.query,
    });
    return createMockResponse(response);
  });

  router.register('POST', '/experiments', async (_url, config) => {
    await delay(150);

    // config.data가 이미 객체인 경우 그대로 사용, 문자열인 경우 파싱
    let data: any;
    if (typeof config?.data === 'string') {
      data = JSON.parse(config.data);
    } else {
      data = config?.data || {};
    }

    const { projectId, experimentName, description } = data;

    if (!projectId) {
      return createMockResponse({ message: 'Project ID is required' }, 400);
    }

    if (!experimentName) {
      return createMockResponse({ message: 'Experiment name is required' }, 400);
    }

    const newExperiment = createExperimentMock(Number(projectId), experimentName, description || '');

    return createMockResponse({
      id: newExperiment.id,
      name: newExperiment.name,
      description: newExperiment.description,
      projectId: newExperiment.projectId,
      createdBy: newExperiment.creator,
      createdAt: new Date(newExperiment.createdAt).toISOString(),
    });
  });

  router.register('PATCH', '/experiments/:id', async (url, config) => {
    await delay(150);
    const experimentId = Number(url.split('/experiments/')[1].split('?')[0]);

    let data: any;
    if (typeof config?.data === 'string') {
      data = JSON.parse(config.data);
    } else {
      data = config?.data || {};
    }

    const updatedExperiment = updateExperimentMock(experimentId, data);
    if (!updatedExperiment) {
      return createMockResponse({ message: 'Experiment not found' }, 404);
    }

    return createMockResponse({
      id: updatedExperiment.id,
      name: updatedExperiment.name,
      description: updatedExperiment.description,
      projectId: updatedExperiment.projectId,
      createdBy: updatedExperiment.creator,
      createdAt: new Date(updatedExperiment.createdAt).toISOString(),
    });
  });

  router.register('POST', '/runs', async (_url, config) => {
    await delay(150);

    // config.data가 이미 객체인 경우 그대로 사용, 문자열인 경우 파싱
    let data: any;
    if (typeof config?.data === 'string') {
      data = JSON.parse(config.data);
    } else {
      data = config?.data || {};
    }

    const {
      experimentId,
      projectId,
      runName,
      datasetId,
      featureSetId,
      datasetSnapshotId,
      featureSetSnapshotId,
      parameterSetId,
      computingResource,
    } = data;

    if (!experimentId) {
      return createMockResponse({ message: 'Experiment ID is required' }, 400);
    }

    if (!runName) {
      return createMockResponse({ message: 'Run name is required' }, 400);
    }

    const projectsData = getProjectsExperimentsMockData();
    const resolvedProject =
      projectsData.projects.find((project) => projectId && project.id === Number(projectId)) ??
      projectsData.projects.find((project) =>
        project.experiments.some((experiment) => experiment.id === Number(experimentId))
      );
    const resolvedProjectId = Number(projectId) || resolvedProject?.id;

    if (!resolvedProjectId) {
      return createMockResponse({ message: 'Project not found' }, 404);
    }

    // 프로젝트와 실험 이름 찾기
    const projectInfo = getProjectMockData(resolvedProjectId);
    if (!projectInfo) {
      return createMockResponse({ message: 'Project not found' }, 404);
    }

    // 실험 정보 찾기
    const experimentsData = getExperimentsMockData({ projectId: resolvedProjectId });
    const experiment = experimentsData.experiments.find((e: any) => e.id === Number(experimentId));
    if (!experiment) {
      return createMockResponse({ message: 'Experiment not found' }, 404);
    }

    const newRun = createRunMock({
      name: runName,
      projectId: resolvedProjectId,
      projectName: projectInfo.name,
      experimentId: Number(experimentId),
      experimentName: experiment.name,
      datasetId: datasetId !== undefined && datasetId !== null ? String(datasetId) : undefined,
      featureSetId: featureSetId !== undefined && featureSetId !== null ? String(featureSetId) : undefined,
      datasetSnapshotId:
        datasetSnapshotId !== undefined && datasetSnapshotId !== null ? Number(datasetSnapshotId) : null,
      featureSetSnapshotId:
        featureSetSnapshotId !== undefined && featureSetSnapshotId !== null ? Number(featureSetSnapshotId) : null,
      parameterSetId,
      computingResource: computingResource || 'c4-basic',
    });

    return createMockResponse({
      id: Number(newRun.id),
      runName: newRun.name,
      name: newRun.name,
      status: newRun.status,
      startTime: newRun.startTime,
    });
  });

  router.register('POST', '/runs/:id/execute', async (url, _config) => {
    await delay(100);
    const runId = url.split('/runs/')[1].split('/execute')[0];

    const result = executeRunMock(runId);
    if (!result) {
      return createMockResponse({ message: 'Run not found' }, 404);
    }

    return createMockResponse({
      id: result.id,
      status: result.status,
      message: 'Run execution started',
    });
  });

  router.register('POST', '/runs/:id/sync', async (url, _config) => {
    await delay(120);
    const runIdSegment = decodeURIComponent(url.split('/runs/')[1].split('/sync')[0].split('?')[0]);
    const detail = getRunDetailMock(runIdSegment);
    if (!detail) {
      return createMockResponse({ message: 'Run not found' }, 404);
    }
    return createMockResponse(detail);
  });

  router.register('POST', '/runs/:id/executions', async (url, config) => {
    await delay(120);
    const runId = url.split('/runs/')[1].split('/executions')[0];
    const request = parseMockRequestData<any>(config?.data);
    const response = startRunExecutionMock(runId, request);

    if (!response) {
      return createMockResponse({ message: 'Run not found' }, 404);
    }

    return createMockResponse(response);
  });

  router.register('GET', '/runs/:id/executions/:executionId', async (url, _config) => {
    await delay(60);
    const [, rest] = url.split('/runs/');
    const [runId, executionPath] = rest.split('/executions/');
    const executionId = executionPath.split('?')[0];
    const response = getRunExecutionMock(decodeURIComponent(runId), decodeURIComponent(executionId));

    if (!response) {
      return createMockResponse({ message: 'Execution not found' }, 404);
    }

    return createMockResponse(response);
  });

  router.register('POST', '/runs/:id/executions/:executionId/cancel', async (url, _config) => {
    await delay(250);
    const [, rest] = url.split('/runs/');
    const [runId, executionPath] = rest.split('/executions/');
    const executionId = executionPath.split('/cancel')[0];
    const response = cancelRunExecutionMock(decodeURIComponent(runId), decodeURIComponent(executionId));

    if (!response) {
      return createMockResponse({ message: 'Execution not found' }, 404);
    }

    return createMockResponse(response);
  });

  router.register('PUT', '/runs/:id', async (url, config) => {
    await delay(100);
    const runId = url.split('/runs/')[1].split('?')[0];

    let data: any;
    if (typeof config?.data === 'string') {
      data = JSON.parse(config.data);
    } else {
      data = config?.data || {};
    }

    const { name, runName } = data;
    const nextName = name || runName;
    if (!nextName) {
      return createMockResponse({ message: 'Run name is required' }, 400);
    }

    const result = updateRunNameMock(runId, nextName);
    if (!result) {
      return createMockResponse({ message: 'Run not found' }, 404);
    }

    return createMockResponse({
      id: Number(result.id),
      name: result.name,
      runName: result.name,
      message: 'Run name updated successfully',
    });
  });

  router.register('GET', '/runs/compare', async (_url, config) => {
    await delay(100);
    const params = config?.params || {};
    const runIds = Array.isArray(params.runIds) ? params.runIds.map(Number) : [];
    const response = compareRunsMockData(runIds);
    return createMockResponse(response);
  });

  router.register('GET', '/runs/:id/executions', async (url, _config) => {
    await delay(60);
    const runId = decodeURIComponent(url.split('/runs/')[1].split('/executions')[0]);
    const response = getRunLatestExecutionMock(runId);
    return createMockResponse(response);
  });

  router.register('GET', '/runs/:id/metrics', async (url, _config) => {
    await delay(60);
    const sourceRunId = decodeURIComponent(url.split('/runs/')[1].split('/metrics')[0]);
    const response = getRunMetricsMock(sourceRunId);
    if (!response) {
      return createMockResponse({ message: 'Run metrics not found' }, 404);
    }
    return createMockResponse(response);
  });

  router.register('GET', '/runs/:id', async (url, _config) => {
    await delay(60);
    const runId = url.split('/runs/')[1].split('?')[0];
    const response = getRunDetailMock(runId);
    if (!response) {
      return createMockResponse({ message: 'Run not found' }, 404);
    }
    return createMockResponse(response);
  });

  router.register('GET', '/runs', async (_url, config) => {
    await delay(80);
    const params = config?.params || {};

    if (params.experimentId) {
      const response = getRunsMockData({
        experimentId: Number(params.experimentId),
        query: params.query,
      });
      return createMockResponse(response);
    }

    return createMockResponse({ pagination: { page: 1, size: 100, totalCount: 0, totalPages: 0 }, runs: [] });
  });

  router.register('GET', '/search/integration', async (_url, config) => {
    await delay(80);
    const params = config?.params || {};

    // assetTypes가 단일 문자열로 올 수 있으므로 배열로 변환
    const normalizedParams = {
      ...params,
      assetTypes: params.assetTypes
        ? Array.isArray(params.assetTypes)
          ? params.assetTypes
          : [params.assetTypes]
        : undefined,
    };

    const response = searchIntegrationMockData(normalizedParams);
    return createMockResponse(response);
  });

  router.register('GET', '/assets/:id', async (url, config) => {
    await delay(60);
    const assetId = Number(url.split('/assets/')[1].split('?')[0]);
    const type = config?.params?.type;

    if (!type) {
      return createMockResponse({ message: 'type parameter is required' }, 400);
    }

    const detail = getExperimentAssetDetail(assetId, type);

    if (!detail) {
      return createMockResponse({ message: 'Asset not found' }, 404);
    }

    return createMockResponse(detail);
  });

  return {
    get<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('GET', url, config as any) as any;
    },
    post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('POST', url, { data, ...config } as any) as any);
    },
    patch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('PATCH', url, { data, ...config } as any) as any);
    },
    put<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('PUT', url, { data, ...config } as any) as any);
    },
    delete<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('DELETE', url, config as any) as any);
    },
  };
};

export const createMockQualityClient = (): Partial<AxiosInstance> => {
  const router = new MockRouter();

  router.register('GET', '/stats/row-counts', async (_url, config) => {
    await delay(40);
    const params = parseQueryParams(config?.params);
    const response = getDataZoneRowCountMockData({
      dataZone: params.dataZone,
      countType: params.countType,
      fromDate: params.fromDate,
      toDate: params.toDate,
      tableId: params.tableId ? Number(params.tableId) : undefined,
    });
    return createMockResponse(response);
  });

  router.register('GET', '/stats/quality-trend', async (_url, config) => {
    await delay(40);
    const params = parseQueryParams(config?.params);
    const response = getQualityTrendMockData({
      fromDate: params.fromDate,
      toDate: params.toDate,
      dataZone: params.dataZone,
    });
    return createMockResponse(response);
  });

  router.register('GET', '/stats/tables', async (_url, config) => {
    await delay(30);
    const params = parseQueryParams(config?.params);
    const response = getStatsTablesMockData({
      dataZone: params.dataZone,
      namespace: params.namespace,
      sortBy: params.sortBy,
    });
    return createMockResponse(response);
  });

  router.register('GET', '/stats/table-quality', async (_url, config) => {
    await delay(50);
    const params = parseQueryParams(config?.params);
    const response = getTableQualityMockData({
      dataZone: params.dataZone,
      service: params.service,
      fromDate: params.fromDate,
      toDate: params.toDate,
      tableId: params.tableId ? Number(params.tableId) : undefined,
      qualityLevel: params.qualityLevel,
      sortBy: params.sortBy,
      page: params.page ? Number(params.page) : undefined,
      pageSize: params.pageSize ? Number(params.pageSize) : undefined,
    });
    return createMockResponse(response);
  });

  router.register('GET', '/stats/column-quality', async (_url, config) => {
    await delay(50);
    const params = parseQueryParams(config?.params);
    const response = getColumnQualityMockData({
      dataZone: params.dataZone,
      service: params.service,
      fromDate: params.fromDate,
      toDate: params.toDate,
      tableId: params.tableId ? Number(params.tableId) : undefined,
      columnName: params.columnName,
      qualityLevel: params.qualityLevel,
      sortBy: params.sortBy || '-collectedAt',
      page: params.page ? Number(params.page) : 1,
      pageSize: params.pageSize ? Number(params.pageSize) : 20,
    });
    return createMockResponse(response);
  });

  router.register('GET', '/stats/categories', async () => {
    await delay(20);
    const response = getQualityCategoriesMockData();
    return createMockResponse(response);
  });

  return {
    get<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('GET', url, { params: config?.params } as any) as any;
    },
    post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('POST', url, { data, ...config } as any) as any);
    },
    patch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('PATCH', url, { data, ...config } as any) as any);
    },
    put<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('PUT', url, { data, ...config } as any) as any);
    },
    delete<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('DELETE', url, config as any) as any);
    },
  };
};

export const createMockSystemClient = (): Partial<AxiosInstance> => {
  const router = new MockRouter();

  router.register('GET', '/monitoring/metrics/labels/Hostname/values', async () => {
    await delay(20);
    return createMockResponse(['demo-worker-a100-01', 'demo-worker-a100-02', 'demo-worker-l4-01']);
  });

  router.register('GET', '/monitoring/metrics/instant', async (_url, config) => {
    await delay(30);
    const params = parseQueryParams(config?.params);
    const query = params.query || '';
    const response = getPrometheusInstantMockData(query);
    return createMockResponse(response);
  });

  router.register('GET', '/monitoring/metrics/range', async (_url, config) => {
    await delay(50);
    const params = parseQueryParams(config?.params);
    const query = params.query || '';
    const start = Number(params.start) || Math.floor(Date.now() / 1000) - 3600;
    const end = Number(params.end) || Math.floor(Date.now() / 1000);
    const step = Number(params.step) || 60;
    const response = getPrometheusRangeMockData(query, start, end, step);
    return createMockResponse(response);
  });

  return {
    get<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('GET', url, config as any) as any;
    },
    post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('POST', url, { data, ...config } as any) as any);
    },
    patch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('PATCH', url, { data, ...config } as any) as any);
    },
    put<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('PUT', url, { data, ...config } as any) as any);
    },
    delete<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return delay(100).then(() => router.match('DELETE', url, config as any) as any);
    },
  };
};

export const createMockDeployClient = (): Partial<AxiosInstance> => {
  const router = new MockRouter();

  router.register('POST', '/api/v1/deployments/recent/by-run-ids', async (_url, config) => {
    await delay(80);
    const request = parseMockRequestData<DeploymentsByRunIdsRequest>(config?.data);
    return createMockResponse(getDeploymentsByRunIdsMock(request));
  });

  router.register('POST', '/api/v1/deployments', async (_url, config) => {
    await delay(300);
    const request = parseMockRequestData<CreateDeploymentRequest>(config?.data);
    return createMockResponse(createDeploymentMock(request));
  });

  router.register('GET', '/api/v1/deployments/:id', async (url) => {
    await delay(80);
    const deploymentId = decodeURIComponent(url.split('/api/v1/deployments/')[1].split('?')[0]);
    const detail = getDeploymentStatusMock(deploymentId);

    if (!detail) {
      return createMockResponse({ message: 'Deployment not found' }, 404);
    }

    return createMockResponse(detail);
  });

  router.register('PATCH', '/api/v1/deployments/:id', async (url, config) => {
    await delay(700);
    const deploymentId = decodeURIComponent(url.split('/api/v1/deployments/')[1].split('?')[0]);
    const request = parseMockRequestData<UpdateDeploymentRequest>(config?.data);
    const response = updateDeploymentMock(deploymentId, request);

    if (!response) {
      return createMockResponse({ message: 'Deployment not found' }, 404);
    }

    return createMockResponse(response);
  });

  return {
    get<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('GET', url, config as any) as Promise<R>;
    },
    post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('POST', url, { data, ...config } as any) as Promise<R>;
    },
    patch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('PATCH', url, { data, ...config } as any) as Promise<R>;
    },
  };
};

export const createMockGenericClient = (): Partial<AxiosInstance> => {
  return {
    get<T = any, R = AxiosResponse<T>, D = any>(url: string, _config?: AxiosRequestConfig<D>): Promise<R> {
      console.warn(`[MockClient] Unhandled GET: ${url}`);
      return delay(50).then(() => createMockResponse({ message: 'Mock not implemented' }, 501) as any);
    },
    post<T = any, R = AxiosResponse<T>, D = any>(url: string, _data?: D, _config?: AxiosRequestConfig<D>): Promise<R> {
      console.warn(`[MockClient] Unhandled POST: ${url}`);
      return delay(50).then(() => createMockResponse({ message: 'Mock not implemented' }, 501) as any);
    },
    patch<T = any, R = AxiosResponse<T>, D = any>(url: string, _data?: D, _config?: AxiosRequestConfig<D>): Promise<R> {
      console.warn(`[MockClient] Unhandled PATCH: ${url}`);
      return delay(50).then(() => createMockResponse({ message: 'Mock not implemented' }, 501) as any);
    },
    put<T = any, R = AxiosResponse<T>, D = any>(url: string, _data?: D, _config?: AxiosRequestConfig<D>): Promise<R> {
      console.warn(`[MockClient] Unhandled PUT: ${url}`);
      return delay(50).then(() => createMockResponse({ message: 'Mock not implemented' }, 501) as any);
    },
    delete<T = any, R = AxiosResponse<T>, D = any>(url: string, _config?: AxiosRequestConfig<D>): Promise<R> {
      console.warn(`[MockClient] Unhandled DELETE: ${url}`);
      return delay(50).then(() => createMockResponse({ message: 'Mock not implemented' }, 501) as any);
    },
  };
};

// MLflow API Mock Client
export const createMockMLflowClient = (): Partial<AxiosInstance> => {
  const router = new MockRouter();

  // 아티팩트 콘텐츠 조회
  router.register('GET', '/get-artifact', async (_url, config) => {
    await delay(100);
    const runUuid = config?.params?.run_uuid;
    const path = config?.params?.path;

    if (!runUuid || !path) {
      return createMockResponse({ message: 'run_uuid and path are required' }, 400);
    }

    // Custom runs에서 artifact 찾기
    const customRuns = loadCustomRuns();
    const customRun = customRuns.find((r) => r.id === runUuid);

    let artifact: any = null;

    if (customRun && customRun.artifacts) {
      artifact = customRun.artifacts.find((a: any) => a.path === path);
    }

    // Custom run에서 못 찾으면 기존 mock run에서 찾기
    if (!artifact) {
      const runsMockData = getRunsMockData({});
      const mockRun = runsMockData.runs.find((r: any) => String(r.id) === String(runUuid));

      if (mockRun && mockRun.artifacts && mockRun.artifacts.files) {
        artifact = mockRun.artifacts.files.find((a: any) => a.path === path);
      }
    }

    if (!artifact) {
      return createMockResponse({ message: 'Artifact not found' }, 404);
    }

    // 디렉토리인 경우
    if (artifact.isDir) {
      return {
        data: new Blob(['']),
        status: 200,
        statusText: 'OK',
        headers: { 'content-type': 'text/plain' },
        config: config as any,
      };
    }

    // JSON, TEXT 파일은 content를 Blob으로 반환
    if (artifact.content !== undefined && artifact.content !== null) {
      const contentString =
        typeof artifact.content === 'string' ? artifact.content : JSON.stringify(artifact.content, null, 2);
      const blob = new Blob([contentString], {
        type: artifact.type === 'json' ? 'application/json' : 'text/plain',
      });
      return {
        data: blob,
        status: 200,
        statusText: 'OK',
        headers: {
          'content-type': artifact.type === 'json' ? 'application/json' : 'text/plain',
        },
        config: config as any,
      };
    }

    // content가 없는 경우
    const mockBlob = new Blob(['No content available'], { type: 'text/plain' });
    return {
      data: mockBlob,
      status: 200,
      statusText: 'OK',
      headers: { 'content-type': 'text/plain' },
      config: config as any,
    };
  });

  return {
    get<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('GET', url, config as any) as Promise<R>;
    },
    post<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      const configWithData = { ...config, data } as any;
      return router.match('POST', url, configWithData) as Promise<R>;
    },
    patch<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      const configWithData = { ...config, data } as any;
      return router.match('PATCH', url, configWithData) as Promise<R>;
    },
    put<T = any, R = AxiosResponse<T>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
      const configWithData = { ...config, data } as any;
      return router.match('PUT', url, configWithData) as Promise<R>;
    },
    delete<T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return router.match('DELETE', url, config as any) as Promise<R>;
    },
  };
};
